import Vue from "vue";
import { MeterGreen, Gateway, Device, Machine } from "../../api";
import { NO_REGION_SELECTED, NO_RATE_SELECTED } from "./meters"

const initialState = () => {
 return {
    meters: [],
    metersProjectEui: null,
    metersRequestId: 0,
    metersContextVersion: 0,
    gateways: [],
    mainGateway: null,
    errorGateways: false
  }
}

export const gateways = {
  namespaced: true,
  state: initialState(),
  getters: {
    getAllMeters(state, getters, rootState) {
      return state.metersProjectEui === rootState.project.nameEui ? state.meters : [];
    },
    getMainGateway() {
      return JSON.parse(localStorage.getItem('gateways')).find(gateway => gateway.main).id
    },
    getGateways() {
      return JSON.parse(localStorage.getItem('gateways'))
    },
    getErrorGateways(state) {
      return state.errorGateways
    }
  },
  actions: {
    async fetchGateways({ commit }) {
      try {
        commit("SET_ERROR_GATEWAYS", false)
        const gateways = JSON.parse(localStorage.getItem("gateways"));
        if (!gateways || gateways.length === 0) {
          const response = await Gateway.get();
          const formattedGateways = response.data.map((gateway) => {
            const { name_eui: id, name, main, key } = gateway;
            return { name, main, key, id };
          });
          commit("SET_GATEWAYS", formattedGateways);
          return;
        }
        commit("SET_GATEWAYS", gateways);
      } catch (error) {
        commit("SET_ERROR_GATEWAYS", true)
      }
    },
    async addGateway({ commit }, data) {
      try {
        const { key, id, name } = data;
        await Gateway.post(key, id, name);
        commit("ADD_GATEWAY", data);
      } catch (error) {
        throw error;
      }
    },
    async assignGateway({ commit, state }, id) {
      try {
        const mainAssigned = state.gateways.some((gateway) => gateway.main);
        mainAssigned && (await Gateway.deleteMainGateway());
        await Gateway.assign(id);
        commit("ASSIGN_GATEWAY", id);
      } catch (error) {
        throw error;
      }
    },
    async deleteGateway({ commit }, id) {
      try {
        await Gateway.deletion(id);
        commit("DELETE_GATEWAY", id);
      } catch (error) {
        throw error;
      }
    },
    async fetchMeters({ commit, state, rootState }) {
      const projectEui = rootState.project.nameEui;
      if (!projectEui) return;
      commit('START_METERS_REQUEST');
      const requestId = state.metersRequestId;
      const isCurrent = () => projectEui === rootState.project.nameEui && requestId === state.metersRequestId;
      try {
        const response = await Device.get('Meter');
        const meters = response.data
          .map((meter) => ({
            ...meter,
            id: meter.functions.Basic.assetId,
            name: meter.functions.Basic.displayName,
            rate: meter.functions.RateParameters?.rateType || NO_RATE_SELECTED,
            region: meter.functions.RateParameters?.rateRegion || NO_REGION_SELECTED,
          }))
          .sort((a, b) => (a.name > b.name ? 1 : -1));
        if (isCurrent()) {
          commit('SET_METERS_PROJECT', projectEui);
          commit("SET_METERS", meters);
        }
      } catch (error) {
        if (!isCurrent()) return;
        commit("SET_METERS", []);
        throw { type: 'meter', error }
      }
    },
    async fetchMeter({ }, id) {
      try {
        const response = await Device.getNode(id);
        const meter = response.data;
        meter.id = meter.functions.Basic.assetId;
        meter.name = meter.functions.Basic.displayName;
        return meter;
      } catch (error) {
        throw error
      }
    },
    async addMeters({ commit, state, rootState }, meters) {
      const project = rootState.project.nameEui;
      const contextVersion = state.metersContextVersion;
      try {
        await Promise.all([...meters].map(meter => MeterGreen.addDevice(meter.id, meter.name)))
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        await Promise.all(
          [...meters]
            .filter(meter => meter.rate && meter.region)
            .map(meter => MeterGreen.updateRateInfo(meter.id, meter.rate, meter.region))
          )
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        const newMeters = (await Promise.all([...meters].map(meter => Device.getNode(meter.id))))
                            .map(meter => {
                              return {
                                id: meter.data.functions.Basic.assetId, 
                                name: meter.data.functions.Basic.displayName,
                                rate: meter.data.functions.RateParameters?.rateType || NO_RATE_SELECTED, 
                                region: meter.data.functions.RateParameters?.rateRegion || NO_REGION_SELECTED
                              }
                            })
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        await Promise.all([...meters].map(meter => Machine.addMachine(`${meter.id}-MACHINE`)))
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        commit("ADD_METERS", newMeters);
      } catch (error) {
        throw error.response.data;
      }
    },
    async editMeter({ commit, state, rootState }, meter) {
      const project = rootState.project.nameEui;
      const contextVersion = state.metersContextVersion;
      try {
        await MeterGreen.updateDisplayName(meter.id, meter.name);
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        if (meter.rate && meter.region) 
          await MeterGreen.updateRateInfo(meter.id, meter.rate, meter.region)  
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        const response = await Device.getNode(meter.id);
        const meterToEdit = {};
        meterToEdit.id = response.data.functions.Basic.assetId;
        meterToEdit.name = response.data.functions.Basic.displayName;
        meterToEdit.rate = response.data.functions.RateParameters?.rateType || NO_RATE_SELECTED;
        meterToEdit.region = response.data.functions.RateParameters?.rateRegion || NO_REGION_SELECTED;
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        commit("EDIT_METER", meterToEdit);
      } catch (error) {
        throw error;
      }
    },
    async deleteMeters({ commit, state, rootState }, meter) {
      const project = rootState.project.nameEui;
      const contextVersion = state.metersContextVersion;
      try {
        await MeterGreen.deletion(meter);
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        await Machine.deleteMachine(`${meter}-MACHINE`);
        if (project !== rootState.project.nameEui || contextVersion !== state.metersContextVersion) return;
        commit("DELETE_METER", meter);
      } catch (error) {
        throw error;
      }
    },
  },
  mutations: {
    START_METERS_REQUEST(state) {
      state.metersRequestId += 1;
    },
    SET_METERS_PROJECT(state, projectEui) {
      state.metersProjectEui = projectEui;
    },
    SET_GATEWAYS(state, gateways) {
      localStorage.setItem("gateways", JSON.stringify(gateways));
      state.gateways = JSON.parse(localStorage.getItem("gateways"));
      state.gateways.sort((a, b) => b.main - a.main);
      const foundMainGateway = [...state.gateways].find(
        (gateway) => gateway.main === true
      );
      if (!foundMainGateway) return;
      const { id } = foundMainGateway;
      state.mainGateway = id;
    },
    ADD_GATEWAY(state, data) {
      state.gateways.push(data);
      localStorage.setItem("gateways", JSON.stringify(state.gateways));
    },
    ASSIGN_GATEWAY(state, id) {
      state.gateways.forEach((gateway) => {
        gateway.main = gateway.id === id;
      });
      state.gateways.sort((a, b) => b.main - a.main);
      localStorage.setItem("gateways", JSON.stringify(state.gateways));
    },
    DELETE_GATEWAY(state, id) {
      state.gateways = state.gateways.filter((gateway) => gateway.id !== id);
      localStorage.setItem("gateways", JSON.stringify(state.gateways));
    },
    SET_ERROR_GATEWAYS(state, payload){
      state.errorGateways = payload
    },
    SET_METERS(state, meters) {
      state.meters = meters;
      localStorage.setItem("metersv2", JSON.stringify(meters));
    },
    ADD_METERS(state, meters) {
      state.meters = [...state.meters, ...meters];
      localStorage.setItem("metersv2", JSON.stringify(state.meters));
    },
    EDIT_METER(state, updatedMeter) {
      const index = state.meters.findIndex(
        (meter) => meter.id === updatedMeter.id
      );
      Vue.set(state.meters, index, updatedMeter);
      localStorage.setItem("metersv2", JSON.stringify(state.meters));
    },
    DELETE_METER(state, meterId) {
      state.meters = state.meters.filter((meter) => meter.id !== meterId);
      localStorage.setItem("metersv2", JSON.stringify(state.meters));
    },
    RESET(state) {
      Object.assign(state, initialState(), {
        metersRequestId: state.metersRequestId + 1,
        metersContextVersion: state.metersContextVersion + 1,
      });
    }
  },
};
